import { FC, useEffect } from "react";
import { Dropdown, DropdownItem } from "react-migration/components/Dropdown";

import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { useBetaFeatureFlag } from "react-migration/lib/user/useFeatureFlag";
import Feature from "src/js/stores/user/Feature";
import {
  PerAreaPriceModes,
  PerUnitPriceModes,
  PriceModes,
  ZooplaPricePerUnitModes,
  ZooplaPricePerAreaModes,
} from "../../../typings/PriceModes";
import { PriceTypes } from "../../../typings/PriceTypes";
import { LastValueType } from "react-migration/lib/typings/Zoopla";
import { isRental } from "../../MapLayer/ComparablesLayer/zooplaComparablesLayerUtil";
import hasFeature from "src/js/stores/user/actions/hasFeature";

export interface PriceModeProps {
  priceMode: PriceModes;
  priceType: PriceTypes;
  setPriceMode: (priceMode: PriceModes) => void;

  priceSource: LastValueType | "All";
  setPriceSource: (priceSource: LastValueType | "All") => void;

  useZooplaOptions?: boolean;

  showAllOptions?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  eventTracking?: (values: { filterName: string; filterState: any }) => void;
}

export const PriceMode: FC<PriceModeProps> = ({
  priceMode,
  priceType,
  setPriceMode,
  priceSource,
  setPriceSource,
  useZooplaOptions,
  showAllOptions,
  eventTracking,
}) => {
  const { t } = useTranslation();
  const hometrackV2 = useBetaFeatureFlag(Feature.hometrackv2) || hasFeature(Feature.zooplaFullAccess);
  const usAccess = hasFeature(Feature.usAccess);
  const hasHometrack = hometrackV2 && !usAccess;

  const PRICE_TEXT = hasHometrack
    ? t("comparables.filters.displayBy.price")
    : t("comparables.filters.displayBy.price_legacy");
  const EST_PRICE_TEXT = hasHometrack
    ? t("comparables.filters.displayBy.est_price")
    : t("comparables.filters.displayBy.est_price_legacy");

  // Note: ZOOPLA_LAST_KNOWN_VALUE_TEXT is commented out in the dropdownZooplaOptions
  const ZOOPLA_ESTIMATED_VALUE_TEXT = hasHometrack
    ? t("comparables.filters.displayBy.zoopla_valuation_current")
    : t("comparables.filters.displayBy.zoopla_valuation_current_legacy");
  const ZOOPLA_RENTAL_ESTIMATE_TEXT = hasHometrack
    ? t("comparables.filters.displayBy.zoopla_rental_estimate")
    : t("comparables.filters.displayBy.zoopla_rental_estimate_legacy");
  const ZOOPLA_LAST_KNOWN_RENTAL_VALUE_TEXT = hasHometrack
    ? t("comparables.filters.displayBy.zoopla_rental_asking")
    : t("comparables.filters.displayBy.zoopla_rental_asking_legacy");

  useEffect(() => {
    const otherPriceType = priceType === PriceTypes.PER_AREA ? PriceTypes.PER_UNIT : PriceTypes.PER_AREA;
    const defaultTypes = {
      [PriceTypes.PER_AREA]: PerAreaPriceModes.PPSM,
      [PriceTypes.PER_UNIT]: PerUnitPriceModes.PRICE,
    };
    const availableTypes: Array<Record<PriceTypes, PriceModes>> = [
      {
        [PriceTypes.PER_AREA]: PerAreaPriceModes.PPSM,
        [PriceTypes.PER_UNIT]: PerUnitPriceModes.PRICE,
      },
      {
        [PriceTypes.PER_AREA]: PerAreaPriceModes.EPPSM,
        [PriceTypes.PER_UNIT]: PerUnitPriceModes.EP,
      },
      {
        [PriceTypes.PER_AREA]: ZooplaPricePerAreaModes.RENT_ESTIMATE,
        [PriceTypes.PER_UNIT]: ZooplaPricePerUnitModes.RENT_ESTIMATE,
      },
      {
        [PriceTypes.PER_AREA]: ZooplaPricePerAreaModes.VAL_ESTIMATE,
        [PriceTypes.PER_UNIT]: ZooplaPricePerUnitModes.VAL_ESTIMATE,
      },
    ];
    if (!availableTypes.some((type) => type[priceType] === priceMode)) {
      setPriceMode(
        availableTypes.find((type) => type[otherPriceType] === priceMode)?.[priceType] || defaultTypes[priceType]
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceType]);

  const dropdownOptions: DropdownItem<PriceModes>[] = [
    {
      label: PRICE_TEXT,
      value: priceType === PriceTypes.PER_UNIT ? PerUnitPriceModes.PRICE : PerAreaPriceModes.PPSM,
      description: hasHometrack ? (
        <span className="text-xs text-neutral-500">{t("comparables.filters.displayBy.price.description")}</span>
      ) : null,
    },
    {
      label: EST_PRICE_TEXT,
      value: priceType === PriceTypes.PER_UNIT ? PerUnitPriceModes.EP : PerAreaPriceModes.EPPSM,
      description: hasHometrack ? (
        <span className="text-xs text-neutral-500">{t("comparables.filters.displayBy.est_price.description")}</span>
      ) : null,
    },
  ];

  const dropdownZooplaOptions: DropdownItem<PriceModes>[] = [
    // {
    //   label: ZOOPLA_LAST_KNOWN_VALUE_TEXT,
    //   value:
    //     priceType === PriceTypes.PER_UNIT
    //       ? ZooplaPricePerUnitModes.VAL_LAST
    //       : ZooplaPricePerAreaModes.VAL_LAST,
    //   description: (
    //     <span className="text-xs text-neutral-500">
    //       {t("comparables.filters.displayBy.zoopla_valuation_per_unit_last_legacy.description")}
    //     </span>
    //   ),
    // },
    {
      label: ZOOPLA_ESTIMATED_VALUE_TEXT,
      value:
        priceType === PriceTypes.PER_UNIT ? ZooplaPricePerUnitModes.VAL_ESTIMATE : ZooplaPricePerAreaModes.VAL_ESTIMATE,
      description: hasHometrack ? (
        <span className="text-xs text-neutral-500">
          {t("comparables.filters.displayBy.zoopla_valuation_current.description")}
        </span>
      ) : null,
    },
    ...(hasHometrack
      ? [
          {
            label: ZOOPLA_LAST_KNOWN_RENTAL_VALUE_TEXT,
            value:
              priceType === PriceTypes.PER_UNIT
                ? ZooplaPricePerUnitModes.RENT_ASKING
                : ZooplaPricePerAreaModes.RENT_ASKING,
            description: hasHometrack ? (
              <span className="text-xs text-neutral-500">
                {t("comparables.filters.displayBy.zoopla_rental_asking.description")}
              </span>
            ) : null,
          },
        ]
      : []),
    {
      label: ZOOPLA_RENTAL_ESTIMATE_TEXT,
      value:
        priceType === PriceTypes.PER_UNIT
          ? ZooplaPricePerUnitModes.RENT_ESTIMATE
          : ZooplaPricePerAreaModes.RENT_ESTIMATE,
      description: hasHometrack ? (
        <span className="text-xs text-neutral-500">
          {t("comparables.filters.displayBy.zoopla_rental_estimate.description")}
        </span>
      ) : null,
    },
  ];

  // const dropdownZooplaSaleSourceOptions: DropdownItem<LastValueType | "All">[] = [
  //   {
  //     label: t("comparables.map.sidebar.filters.mode.price.zoopla_valuation_all"),
  //     value: "All",
  //   },
  //   {
  //     label: t("comparables.map.sidebar.filters.mode.price.zoopla_valuation_sale"),
  //     value: LastValueType.Sale,
  //   },
  //   {
  //     label: t("comparables.map.sidebar.filters.mode.price.zoopla_valuation_valuation"),
  //     value: LastValueType.Valuation,
  //   },
  //   {
  //     label: t("comparables.map.sidebar.filters.mode.price.zoopla_valuation_asking_price"),
  //     value: LastValueType.AskingPrice,
  //   },
  // ];

  // const dropdownZooplaRentalSourceOptions: DropdownItem<LastValueType | "All">[] = [
  //   {
  //     label: t("comparables.map.sidebar.filters.mode.price.zoopla_valuation_all"),
  //     value: "All",
  //   },
  //   {
  //     label: t("comparables.map.sidebar.filters.mode.price.zoopla_valuation_valuation"),
  //     value: LastValueType.Valuation,
  //   },
  //   {
  //     label: t("comparables.map.sidebar.filters.mode.price.zoopla_valuation_asking_price"),
  //     value: LastValueType.AskingPrice,
  //   },
  // ];

  const combinedOptions = showAllOptions ? [...dropdownOptions, ...dropdownZooplaOptions] : dropdownZooplaOptions;

  return (
    <div className="flex flex-col gap-2">
      <div data-testid="price-mode" className="flex justify-between">
        <h4 className="text-sm font-medium my-auto flex-1 text-left">
          {t("comparables.map.sidebar.filters.mode.price.title")}
        </h4>
        <Dropdown<PriceModes>
          value={priceMode}
          placeholder=""
          dataTestId="price-mode-dropdown"
          name="priceMode"
          classNameContainer="w-[196px] whitespace-nowrap"
          optionsContainerClassName="whitespace-normal"
          handleValueChange={(opt) => {
            eventTracking?.({ filterName: "priceMode", filterState: opt });
            setPriceMode(opt.value);
          }}
          options={useZooplaOptions ? combinedOptions : dropdownOptions}
        />
      </div>

      {/* {useZooplaOptions && !showAllOptions && (
        <div data-testid="price-source" className="flex justify-between">
          <h4 className="text-sm font-medium my-auto flex-1 text-left">
            {t("comparables.map.sidebar.filters.mode.price.source")}
          </h4>
          <Dropdown
            value={priceSource}
            placeholder=""
            dataTestId="price-source-dropdown"
            name="priceSource"
            classNameContainer="w-[196px] whitespace-nowrap"
            optionsContainerClassName="whitespace-normal"
            handleValueChange={(opt) => {
              eventTracking?.({ filterName: "priceSource", filterState: opt });
              setPriceSource(opt.value);
            }}
            options={
              isRental(priceMode)
                ? dropdownZooplaRentalSourceOptions
                : dropdownZooplaSaleSourceOptions
            }
          />
        </div>
      )} */}
    </div>
  );
};
